import type {
  GridFieldType,
  PassportFieldPaletteSource,
} from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  CATALOG_FIELD_CLASS_ID,
  REFERENCE_FIELD_CLASS_ID,
  type PassportBuilderFieldClassDto,
  type PassportBuilderFieldPropsDto,
  type PassportBuilderObjectTypeFieldDto,
  type PassportBuilderFieldTableViewOrderDto,
} from './objectTypeFields'

const PASSPORT_GRID_COLS = 12

const numberOrDefault = (value: unknown, fallback: number): number => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

const positiveNumberOrNull = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

const viewTypeOrDefault = (value: unknown): 'value' | 'table' => {
  return value === 'table' ? 'table' : 'value'
}

const classByFieldType = (fieldClasses: PassportBuilderFieldClassDto[]): Map<number, number> => {
  const map = new Map<number, number>()

  for (const fieldClass of fieldClasses) {
    for (const type of fieldClass.types ?? []) {
      map.set(type.id, fieldClass.id)
    }
  }

  return map
}

const paletteSourceForField = (
  field: PassportBuilderObjectTypeFieldDto,
  typeClassId?: number,
): PassportFieldPaletteSource => {
  if (typeClassId === CATALOG_FIELD_CLASS_ID || Number(field.catalog_id) > 0) return 'parent'
  if (typeClassId === REFERENCE_FIELD_CLASS_ID || Number(field.reference_id) > 0) return 'element'
  return 'own'
}

const inventoryTypeForField = (
  field: PassportBuilderObjectTypeFieldDto,
  typeClassId?: number,
): number => {
  if (typeClassId === CATALOG_FIELD_CLASS_ID) return Number(field.catalog_id) || 0
  if (typeClassId === REFERENCE_FIELD_CLASS_ID) return Number(field.reference_id) || 0
  if (Number(field.catalog_id) > 0) return Number(field.catalog_id)
  if (Number(field.reference_id) > 0) return Number(field.reference_id)
  return Number(field.catalog_objects_type_id) || 0
}

const fieldPropsListForField = (
  field: PassportBuilderObjectTypeFieldDto,
): PassportBuilderFieldPropsDto[] => {
  if (Array.isArray(field.field_props)) return field.field_props
  if (field.field_props) return [field.field_props]
  return []
}

const layoutIdForFieldProps = (fieldProps: PassportBuilderFieldPropsDto): number | null => {
  const id = Number(fieldProps.id)
  return Number.isFinite(id) && id > 0 ? id : null
}

const placementIdForField = (
  field: PassportBuilderObjectTypeFieldDto,
  fieldProps: PassportBuilderFieldPropsDto,
  typeClassId?: number,
  index = 0,
): string => {
  const source = paletteSourceForField(field, typeClassId)
  const layoutId = layoutIdForFieldProps(fieldProps)
  if (source === 'parent' || source === 'element') {
    return `${source}:${field.id}:${layoutId ?? `idx-${index}`}`
  }
  return String(field.id)
}

const layoutNameOrDefault = (
  tableViewOrder: PassportBuilderFieldTableViewOrderDto,
  fallback: string,
): string => {
  const customName = tableViewOrder.name?.trim()
  return customName || fallback
}

/**
 * Преобразует серверную позицию поля в клиентские координаты сетки.
 */
const normalizeTableViewOrder = (
  tableViewOrder: PassportBuilderFieldTableViewOrderDto,
): Pick<GridFieldType, 'row' | 'cell' | 'length'> => {
  const row = Math.max(0, numberOrDefault(tableViewOrder.row, 1) - 1)
  if (tableViewOrder.view_type === 'table') {
    return { row, cell: 0, length: PASSPORT_GRID_COLS }
  }

  const cell = Math.max(0, numberOrDefault(tableViewOrder.column, 1) - 1)
  const length = Math.max(
    1,
    Math.min(PASSPORT_GRID_COLS, numberOrDefault(tableViewOrder.colspan, 4)),
  )

  return { row, cell, length }
}

/**
 * Собирает элемент сетки из `field_props`; `null` означает, что поле не размещено.
 */
export const objectTypeFieldToGridItem = (
  field: PassportBuilderObjectTypeFieldDto,
  fieldProps: PassportBuilderFieldPropsDto,
  fieldClassByTypeId: Map<number, number>,
  index = 0,
): GridFieldType | null => {
  const tableViewOrder = fieldProps?.table_view_order
  if (!tableViewOrder) return null

  const typeClassId = fieldClassByTypeId.get(field.field_type)
  const groupId = positiveNumberOrNull(tableViewOrder.group_id)

  return {
    id: placementIdForField(field, fieldProps, typeClassId, index),
    type: 'field',
    inventory_field_id: field.id,
    field_layout_id: layoutIdForFieldProps(fieldProps),
    inventory_type: inventoryTypeForField(field, typeClassId),
    palette_source: paletteSourceForField(field, typeClassId),
    name: layoutNameOrDefault(tableViewOrder, field.name),
    ...normalizeTableViewOrder(tableViewOrder),
    view_type: viewTypeOrDefault(tableViewOrder.view_type),
    pseudo_sql: fieldProps.pseudo_sql ?? null,
    filter_map: Boolean(fieldProps.filter_map),
    filter_search: Boolean(fieldProps.filter_search),
    filter_dictionary_id: fieldProps.filter_dictionary_id ?? null,
    ...(groupId != null ? { group_id: groupId } : {}),
    ...(typeClassId === REFERENCE_FIELD_CLASS_ID ? { link_field_id: field.id } : {}),
  }
}

/**
 * Собирает шаблон паспорта из нормализованных полей типа объекта.
 */
export const objectTypeFieldsToGridTemplate = (
  fields: PassportBuilderObjectTypeFieldDto[],
  fieldClasses: PassportBuilderFieldClassDto[],
): GridFieldType[] => {
  const fieldClassByTypeId = classByFieldType(fieldClasses)

  return fields
    .flatMap((field) =>
      fieldPropsListForField(field).map((fieldProps, index) =>
        objectTypeFieldToGridItem(field, fieldProps, fieldClassByTypeId, index),
      ),
    )
    .filter((item): item is GridFieldType => item != null)
    .sort((a, b) => a.row - b.row || a.cell - b.cell || a.name.localeCompare(b.name))
}
